import { CONFIG } from "../config";
import { logger } from "./logger";
import { parseJsonSafe } from "./json";

/**
 * Cleans address from history item data
 */
export function cleanAddress(historyItem) {
  try {
    let data = historyItem.data;
    //json parse data
    let dataParsed = parseJsonSafe(data, null);
    if (!dataParsed) {
      return "No geocode available";
    }

    let address = dataParsed.geocode;

    return address;
  } catch (e) {
    return "No geocode available" + e.message;
  }
}

/**
 * Converts millivolts to percentage based on device brand
 */
export function convertMvToPercent(
  mv = null,
  deviceBrand = null,
  addPercent = true
) {
  // Handle null, undefined, or NaN values
  if (mv === null || mv === undefined || isNaN(mv)) {
    return addPercent ? "N/A" : NaN;
  }

  // Convert to number if it's a string
  mv = Number(mv);

  // Double check after conversion
  if (isNaN(mv)) {
    // might be a string like "normal", if it is return that string
    return mv + (addPercent ? "%" : "");
  }

  //check if deviceBrand is full object for device or just the deviceBrand object
  if (deviceBrand && deviceBrand.deviceBrand) {
    deviceBrand = deviceBrand.deviceBrand;
  }

  // if mv is 2 or 3 digits, it might already be a percent, so return it
  if (mv.toString().length <= 3 && mv <= 100) {
    if (!addPercent) {
      return parseInt(mv);
    }

    return mv + "%";
  }

  //load full file content and continue conversion logic
  let batteryMaxMv =
    deviceBrand?.batteryMaxMV || CONFIG.defaultBatteryMaxMv || 4200;
  let batteryMinMv =
    deviceBrand?.batteryMinMV || CONFIG.defaultBatteryMinMv || 3200;

  // Validate that we have valid min/max values
  if (
    !batteryMaxMv ||
    !batteryMinMv ||
    isNaN(batteryMaxMv) ||
    isNaN(batteryMinMv)
  ) {
    logger.warn("Invalid battery min/max values:", {
      batteryMaxMv,
      batteryMinMv,
      deviceBrand,
    });
    return addPercent ? "N/A" : NaN;
  }

  // Ensure max > min to prevent division by zero
  if (batteryMaxMv <= batteryMinMv) {
    logger.warn("Battery max must be greater than min:", {
      batteryMaxMv,
      batteryMinMv,
    });
    return addPercent ? "N/A" : NaN;
  }

  //check if mv is above max, if so return 100%
  if (mv >= batteryMaxMv) {
    if (!addPercent) {
      return 100;
    }
    return "100%";
  }

  //check if mv is below min, if so return 0%
  if (mv <= batteryMinMv) {
    if (!addPercent) {
      return 0;
    }
    return "0%";
  }

  //calculate percentage
  let percent = Math.round(
    ((mv - batteryMinMv) / (batteryMaxMv - batteryMinMv)) * 100
  );

  // Final validation
  if (isNaN(percent)) {
    logger.warn("Calculated percentage is NaN:", {
      mv,
      batteryMinMv,
      batteryMaxMv,
    });
    return addPercent ? "N/A" : NaN;
  }

  if (!addPercent) {
    return percent;
  }

  return percent + "%";
}

/**
 * Converts an HSL colour to a 6-digit hex string.
 *
 * @param {number} h - Hue (0-360)
 * @param {number} s - Saturation (0-100)
 * @param {number} l - Lightness (0-100)
 * @returns {string} Hex colour, e.g. "#8bc34a"
 */
function hslToHex(h, s, l) {
  s /= 100;
  l /= 100;
  const k = (n) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n) => {
    const colour =
      l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    return Math.round(255 * colour)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

/**
 * Converts a battery percentage (0-100) to an expressive colour on a smooth
 * red -> amber -> green gradient. Low levels are red, mid levels shade through
 * orange/yellow, and only near-full levels are a strong green.
 *
 * @param {number|string} percent - Percentage, optionally suffixed with "%"
 * @returns {string} Hex colour, or "#ccc" for unknown/invalid input
 */
export function convertPercentToColour(percent) {
  if (
    percent === null ||
    percent === undefined ||
    percent === "" ||
    percent === "N/A"
  ) {
    return "#ccc";
  }

  // Remove % sign if it exists and normalize to a number
  if (typeof percent === "string") {
    percent = Number(percent.replace("%", ""));
  }

  if (isNaN(percent)) {
    return "#ccc";
  }

  // Clamp to 0-100 and map onto a hue range of 0 (red) -> 120 (green).
  const clamped = Math.max(0, Math.min(100, percent));
  const hue = (clamped / 100) * 120;

  return hslToHex(hue, 70, 45);
}
